import { DatePipe } from '@angular/common';
import { Component, OnInit, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { AdminService, UserSummary } from '../../../core/admin/admin';
import { UserModal } from '../user-modal/user-modal';

const PAGE_SIZE = 20;

/** Exactly one modal (or none) can be open at a time — a discriminated
 *  union guarantees that structurally, rather than relying on two
 *  independent booleans that a future change could set true together. */
type ModalState = { mode: 'edit'; userId: number } | { mode: 'create' } | null;

@Component({
  selector: 'app-admin-users-list',
  imports: [DatePipe, ReactiveFormsModule, RouterLink, UserModal],
  templateUrl: './admin-users-list.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './admin-users-list.css',
})
export class AdminUsersList implements OnInit {
  private readonly admin = inject(AdminService);

  readonly loading = signal(true);
  readonly errorMessage = signal<string | null>(null);
  readonly items = signal<UserSummary[]>([]);
  readonly total = signal(0);
  readonly page = signal(1);

  readonly totalPages = computed(() => Math.max(1, Math.ceil(this.total() / PAGE_SIZE)));

  readonly searchForm = new FormGroup({
    q: new FormControl('', { nonNullable: true }),
  });

  readonly modal = signal<ModalState>(null);
  readonly editingUserId = computed(() => {
    const m = this.modal();
    return m?.mode === 'edit' ? m.userId : null;
  });
  readonly isCreating = computed(() => this.modal()?.mode === 'create');

  async ngOnInit(): Promise<void> {
    await this.load();
  }

  async search(): Promise<void> {
    this.page.set(1);
    await this.load();
  }

  async goToPage(page: number): Promise<void> {
    if (page < 1 || page > this.totalPages()) return;
    this.page.set(page);
    await this.load();
  }

  open(user: UserSummary): void {
    this.modal.set({ mode: 'edit', userId: user.id });
  }

  openCreate(): void {
    this.modal.set({ mode: 'create' });
  }

  onModalClosed(): void {
    this.modal.set(null);
  }

  async onModalChanged(): Promise<void> {
    await this.load();
  }

  private async load(): Promise<void> {
    this.loading.set(true);
    this.errorMessage.set(null);
    try {
      const q = this.searchForm.getRawValue().q.trim();
      const result = await this.admin.listUsers({
        q: q || undefined,
        page: this.page(),
        limit: PAGE_SIZE,
      });
      this.items.set(result.items);
      this.total.set(result.total);
    } catch {
      this.errorMessage.set('Could not load users — try again in a moment.');
    } finally {
      this.loading.set(false);
    }
  }
}
